import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tap-to-toggle voice input wrapper around the Web Speech API. Streams
 * interim transcripts live so the composer reflects speech as the user
 * talks, then commits the final transcript once recognition ends.
 *
 * All side effects happen inside the browser — no network calls, no
 * backend dependency, and latency is typically < 100ms from tap to first
 * audio event.
 */
export function useVoiceInput({ lang = "en-US" } = {}) {
    const [isRecording, setIsRecording] = useState(false);
    const [interim, setInterim] = useState("");
    const [error, setError] = useState(null);

    const recognitionRef = useRef(null);
    const finalRef = useRef("");
    const lastInterimRef = useRef("");
    const onCommitRef = useRef(null);

    const isSupported =
        typeof window !== "undefined" &&
        !!(window.SpeechRecognition || window.webkitSpeechRecognition);

    const stop = useCallback(() => {
        const rec = recognitionRef.current;
        if (!rec) return;
        try {
            rec.stop();
        } catch {
            /* noop */
        }
    }, []);

    const start = useCallback(
        (onCommit) => {
            if (!isSupported) {
                setError("unsupported");
                return false;
            }
            if (isRecording) return false;

            const Ctor =
                window.SpeechRecognition || window.webkitSpeechRecognition;
            const rec = new Ctor();
            rec.continuous = true;
            rec.interimResults = true;
            rec.lang = lang;

            finalRef.current = "";
            lastInterimRef.current = "";
            onCommitRef.current =
                typeof onCommit === "function" ? onCommit : null;
            setInterim("");
            setError(null);

            rec.onstart = () => setIsRecording(true);

            rec.onresult = (ev) => {
                let interimText = "";
                let finalText = "";
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    const r = ev.results[i];
                    if (r.isFinal) finalText += r[0].transcript;
                    else interimText += r[0].transcript;
                }
                if (finalText) finalRef.current += finalText;
                lastInterimRef.current = interimText;
                setInterim(interimText);
            };

            rec.onerror = (ev) => {
                setError(ev?.error || "unknown");
            };

            rec.onend = () => {
                setIsRecording(false);
                setInterim("");
                // Always commit *something* the user saw — final text if the
                // engine flagged it, otherwise the last interim. Browsers
                // routinely end recognition without ever marking a result as
                // `isFinal` for short or quick speech, and losing that text
                // reads as "the voice message disappeared".
                const combined = (
                    finalRef.current + " " + lastInterimRef.current
                ).trim();
                if (combined && onCommitRef.current) {
                    onCommitRef.current(combined);
                }
                finalRef.current = "";
                lastInterimRef.current = "";
                onCommitRef.current = null;
                recognitionRef.current = null;
            };

            recognitionRef.current = rec;
            try {
                rec.start();
                return true;
            } catch {
                setError("start-failed");
                return false;
            }
        },
        [isSupported, isRecording, lang],
    );

    // Clean up if the component unmounts mid-session.
    useEffect(() => {
        return () => {
            const rec = recognitionRef.current;
            if (!rec) return;
            try {
                rec.abort();
            } catch {
                /* noop */
            }
        };
    }, []);

    const reset = useCallback(() => setError(null), []);

    return { isSupported, isRecording, interim, start, stop, error, reset };
}
